"use client";

import { useTranslations } from "next-intl";
import { SYNERGY_LABELS } from "@/config/peptide-stacks";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SynergyScoreProps {
	score: number;
	size?: "sm" | "md" | "lg";
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SIZE_PX: Record<NonNullable<SynergyScoreProps["size"]>, number> = {
	sm: 60,
	md: 80,
	lg: 100,
};

/**
 * Color for each score level.
 * 5 → botanical green (brand accent)
 * 4 → lighter green
 * 3 → neutral gray-blue
 * 2 → amber/yellow
 * 1 → red
 */
const SCORE_COLORS: Record<number, string> = {
	5: "oklch(0.62 0.12 155)",
	4: "oklch(0.65 0.10 155)",
	3: "oklch(0.55 0.02 240)",
	2: "oklch(0.65 0.15 80)",
	1: "oklch(0.55 0.15 25)",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SynergyScore({ score, size = "md" }: SynergyScoreProps) {
	const t = useTranslations("stackBuilder");

	// Clamp score to 1–5
	const clamped = Math.max(1, Math.min(5, Math.round(score)));

	const labelKey = SYNERGY_LABELS[clamped] as string | undefined;
	const label = labelKey ? t(`synergy.${labelKey}` as Parameters<typeof t>[0]) : String(clamped);

	const px = SIZE_PX[size];
	const strokeWidth = px * 0.075; // ~6px at md (80px), scales with size
	const radius = (px - strokeWidth) / 2;
	const circumference = 2 * Math.PI * radius;
	const fillRatio = clamped / 5;
	const dashOffset = circumference * (1 - fillRatio);

	const color = SCORE_COLORS[clamped] ?? SCORE_COLORS[3];

	// Font sizes relative to container
	const scoreFontSize = px * 0.28;
	const labelFontSize = px * 0.13;

	const ariaLabel = t("synergyScoreAria", { score: clamped, label });

	return (
		<figure
			aria-label={ariaLabel}
			role="img"
			className="flex flex-col items-center gap-1"
			style={{ width: px }}
		>
			<svg
				width={px}
				height={px}
				viewBox={`0 0 ${px} ${px}`}
				fill="none"
				aria-hidden="true"
				style={{ display: "block" }}
			>
				{/* Track circle */}
				<circle
					cx={px / 2}
					cy={px / 2}
					r={radius}
					stroke="currentColor"
					strokeWidth={strokeWidth}
					className="text-muted"
				/>

				{/* Filled arc — starts at 12 o'clock (top), rotated -90deg */}
				<circle
					cx={px / 2}
					cy={px / 2}
					r={radius}
					stroke={color}
					strokeWidth={strokeWidth}
					strokeLinecap="round"
					strokeDasharray={circumference}
					strokeDashoffset={dashOffset}
					transform={`rotate(-90 ${px / 2} ${px / 2})`}
					style={{
						transition: "stroke-dashoffset 0.8s ease",
					}}
					className="motion-reduce:transition-none"
				/>

				{/* Score number */}
				<text
					x={px / 2}
					y={px / 2}
					textAnchor="middle"
					dominantBaseline="central"
					fontSize={scoreFontSize}
					fontWeight="700"
					fill={color}
					style={{ fontVariantNumeric: "tabular-nums" }}
				>
					{clamped}
				</text>
			</svg>

			{/* Label below the ring */}
			<figcaption
				style={{ fontSize: labelFontSize }}
				className="text-center font-medium leading-tight text-muted-foreground"
			>
				{label}
			</figcaption>
		</figure>
	);
}
